/**
 * data/useFeatures.ts — Features API client (SPEC §3.1, PLAN §2.16).
 *
 * Boundary rule: this folder NEVER imports three.js — it only fetches and
 * returns the pure `GlbFeatures` contract.
 *
 * Fetches the hole geometry stored in the `features` JSONB column:
 *   GET /api/modules/lock_calculator/lock/{itemId}/features
 * The endpoint answers 204 when the item has no features (or no GLB model),
 * in which case the connector is skipped and the scene renders without it.
 *
 * The features are the SSOT for the connector geometry: positions and normals
 * come from the official Rothoblaas IFC models, catalog dimensions from the
 * lock_calculator fixtures. Nothing is recomputed here.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { useApiHeaders } from "../../../../../hooks/useApiHeaders";
import type { ApiHeaders } from "../../../../../http/apiHeaders";
import type { GlbFeatures } from "./types";

/** Session cache: an itemId is fetched once, `null` = no features available. */
const featuresCache = new Map<string, GlbFeatures | null>();

/**
 * Fetch the features of a single lock item.
 * Returns null on 204 (no features) and on any failure: a missing model must
 * never break the scene (SPEC R6).
 */
async function fetchFeatures(itemId: string, apiHeaders: ApiHeaders): Promise<GlbFeatures | null> {
  const cached = featuresCache.get(itemId);
  if (cached !== undefined) return cached;

  try {
    const response = await fetch(`/api/modules/lock_calculator/lock/${itemId}/features`, {
      credentials: "include",
      redirect: "error",
      headers: apiHeaders,
    });
    if (response.status === 204) {
      featuresCache.set(itemId, null);
      return null;
    }
    if (!response.ok) throw new Error(`features request failed with status ${response.status}`);
    const features = (await response.json()) as GlbFeatures;
    featuresCache.set(itemId, features);
    return features;
  } catch {
    // Un item senza features non e' un errore: la scena rende senza quel lock.
    featuresCache.set(itemId, null);
    return null;
  }
}

/** Result of `useFeatures`: the resolved features plus the in-flight flag. */
export interface FeaturesState {
  /** Features of the selected connectors (only the ones that resolved). */
  features: GlbFeatures[];
  /** True while a fetch is in flight: consumers must not treat [] as final. */
  isLoading: boolean;
}

/**
 * Hook features: returns `GlbFeatures` for the selected connectors only.
 * Must be used inside the lock calculator's FormProvider (i.e. in the
 * subtree of `useLockScene`, T13).
 *
 * @param itemIds Lock item ids (aligned to nConnectors); a null id, or an item
 * with no features stored, produces no entry.
 * @returns FeaturesState: the resolved features and whether a fetch is pending.
 */
export function useFeatures(itemIds: (string | null)[]): FeaturesState {
  const apiHeaders = useApiHeaders();
  const [features, setFeatures] = useState<GlbFeatures[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const loadingIdRef = useRef(0);

  const key = useMemo(() => itemIds.map((id) => id ?? "").join("|"), [itemIds]);

  useEffect(() => {
    const loadingId = ++loadingIdRef.current;
    let isCurrent = true;
    setIsLoading(true);

    Promise.all(itemIds.map((itemId) => (itemId ? fetchFeatures(itemId, apiHeaders) : Promise.resolve(null))))
      .then((resolved) => {
        // Scarta le risposte di una selezione ormai superata.
        if (!isCurrent || loadingId !== loadingIdRef.current) return;
        setFeatures(resolved.filter((entry): entry is GlbFeatures => entry !== null));
        setIsLoading(false);
      })
      .catch(() => {
        if (!isCurrent || loadingId !== loadingIdRef.current) return;
        setFeatures([]);
        setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, apiHeaders]);

  return { features, isLoading };
}
